import { useState, useEffect, useRef } from 'react'
import { useStore } from '@nanostores/react'
import { isSearchOpen } from '@stores/searchStore'
import { isMobileMenuOpen } from '@stores/menuStore'
import { simulateClick } from '@utils/utils.js'
import SearchInputNosto from '@components/Search/SearchInputNosto.jsx'
import { getSearchResults } from '@api/nosto.js'
import SearchIcon from '@components/Search/SearchIcon/SearchIcon.jsx'
import { filterNostoProducts } from '@utils/nostoValidationUtils.js'
import { getNostoSessionParamsWithFallback } from '@utils/nostoSessionUtils.js'
import { recordSearchSubmit, recordSearchAutocomplete } from '@utils/nostoTracking.js'

const NavSearchNosto = () => {
    const [isHovered, setIsHovered] = useState(false)
    const [query, setQuery] = useState('')
    const [results, setResults] = useState([])
    const [loading, setLoading] = useState(false)
    const [hitCount, setHitCount] = useState(0)
    const [suggestions, setSuggestions] = useState([])
    const [searched, setSearched] = useState(false)
    const debounceRef = useRef()

    const $isSearchOpen = useStore(isSearchOpen || false)
    const $isMobileMenuOpen = useStore(isMobileMenuOpen || false)

    useEffect(() => {
        if ($isSearchOpen) {
            const main = document.querySelector('main')
            if (main) {
                main.classList.remove('menu-overlay')
            }
            const menuClose = document.querySelector('.menu-close')
            if ($isMobileMenuOpen && menuClose) {
                simulateClick(menuClose)
            }
        }
    }, [$isSearchOpen])

    const handleSearch = async (searchTerm = query) => {
        setSuggestions([])
        if (!searchTerm.trim()) {
            setResults([])
            setHitCount(0)
            setSearched(false)
            return
        }
        setLoading(true)

        // Get session params for personalization with timeout
        let sessionParams = null;
        try {
            const sessionPromise = getNostoSessionParamsWithFallback();
            const timeoutPromise = new Promise((resolve) => setTimeout(() => resolve(null), 3000));
            sessionParams = await Promise.race([sessionPromise, timeoutPromise]);
        } catch (error) {
            sessionParams = null;
        }

        const searchResults = await getSearchResults(searchTerm, 500, sessionParams)
        // console.log('Raw Nosto search results for "' + searchTerm + '":', searchResults)

        const hits = searchResults?.data?.search?.products?.hits || []
        // console.log('Raw hits from Nosto for "' + searchTerm + '":', hits.length, hits)

        const filtered = filterNostoProducts(hits, {
            includeOutOfStock: false,
            enableLogging: true,
            context: `Nav Search: "${searchTerm}"`
        })
        // console.log('Filtered results for "' + searchTerm + '":', filtered.length, filtered)

        setResults(filtered)
        setHitCount(filtered.length)
        setLoading(false)
        setSearched(true)

        // Record search submit analytics
        if (filtered.length > 0) {
            recordSearchSubmit(searchTerm, filtered);
        }

        // Close search overlay and redirect to search results page
        isSearchOpen.set(false)

        // Use a more graceful redirect to prevent infinite loops
        setTimeout(() => {
            window.location.href = `/search/${encodeURIComponent(searchTerm)}`
        }, 100)
    }

    const handleInput = (val) => {
        clearTimeout(debounceRef.current)
        if (!val.trim()) {
            setSuggestions([])
            setSearched(false)
            return
        }
        debounceRef.current = setTimeout(async () => {
            // Get session params for personalization with timeout
            let sessionParams = null;
            try {
                const sessionPromise = getNostoSessionParamsWithFallback();
                const timeoutPromise = new Promise((resolve) => setTimeout(() => resolve(null), 3000));
                sessionParams = await Promise.race([sessionPromise, timeoutPromise]);
            } catch (error) {
                sessionParams = null;
            }

            // PERFORMANCE OPTIMIZATION: Limit nav search suggestions to improve performance
            const results = await getSearchResults(val, 6, sessionParams)
            const keywords = results?.data?.search?.keywords?.hits || []
            if (Array.isArray(keywords) && keywords.length > 0) {
                setSuggestions(keywords.map(k => k.keyword))
            } else {
                setSuggestions([])
            }

            // Record autocomplete analytics if we have product results
            if (results?.data?.search?.products?.hits?.length > 0) {
                const hits = results.data.search.products.hits;
                const filtered = filterNostoProducts(hits, {
                    includeOutOfStock: false,
                    enableLogging: true,
                    context: `Nav Search Autocomplete: "${val}"`
                });

                if (filtered.length > 0) {
                    recordSearchAutocomplete(val, filtered);
                }
            }
        }, 300)
    }

    const handleSuggestionClick = (keyword) => {
        setQuery(keyword)
        setSuggestions([])
        handleSearch(keyword)
    }

    const toggleSearch = () => {
        console.log('🔍 NavSearchNosto - toggleSearch called');
        console.log('🔍 NavSearchNosto - Current isSearchOpen state:', $isSearchOpen);
        console.log('🔍 NavSearchNosto - Setting isSearchOpen to:', !$isSearchOpen);
        isSearchOpen.set(!$isSearchOpen)
    }

    return (
        <div className="nav-search relative mr-8 lg:mr-4 flex items-center nav-search-nosto">
            <div className="w-full">
                <SearchInputNosto
                    value={query}
                    onChange={setQuery}
                    onSearch={() => handleSearch(query)}
                    onInput={handleInput}
                    suggestions={suggestions}
                    onSuggestionClick={handleSuggestionClick}
                    className={`search-input items-center lg:w-48 xl:w-64 2xl:w-72 h-8 bg-[#EFEFEF] rounded-sm border-1 border px-2 hover:cursor-pointer hover:border-blue-400 hover:border-2 lg:flex text-white ${
                        isHovered ? 'border-blue-400' : 'border-oat'
                    }`}
                    onClick={toggleSearch}
                />
            </div>

            <div
                className="search-icon ml-auto absolute cursor-pointer mr-4 lg:left-[88%] xl:left-[90%] 2xl:left-[90%] lg:mr-0"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={toggleSearch}
            >
                <SearchIcon width="w-[18px]" />
            </div>
        </div>
    )
}

export default NavSearchNosto
